import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AdminSubscriptionsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filter, setFilter] = useState('all'); // all, free, pro, active, expired
  const [searchTerm, setSearchTerm] = useState('');

  // Load subscription data
  const loadData = async () => {
    setLoading(true);
    try {
      // Get all subscriptions
      const subResponse = await axios.get('/subscriptions/admin/all');
      setSubscriptions(subResponse.data.subscriptions || []);

      // Calculate stats from subscriptions
      const allSubs = subResponse.data.subscriptions || [];
      const proCount = allSubs.filter(s => s.plan_type === 'pro').length;
      const freeCount = allSubs.filter(s => s.plan_type === 'free').length;
      const activeCount = allSubs.filter(s => s.status === 'active').length;
      const recentCount = allSubs.filter(s => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return new Date(s.started_at) > thirtyDaysAgo;
      }).length;

      setStats({
        total: allSubs.length,
        pro: proCount,
        free: freeCount,
        active: activeCount,
        recent: recentCount,
        conversionRate: freeCount > 0 ? ((proCount / (proCount + freeCount)) * 100).toFixed(1) : 0
      });
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
      alert('Failed to load subscriptions: ' + error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAdmin]);

  // Filter subscriptions
  const filteredSubscriptions = subscriptions.filter(sub => {
    // Apply filter
    if (filter === 'pro' && sub.plan_type !== 'pro') return false;
    if (filter === 'free' && sub.plan_type !== 'free') return false;
    if (filter === 'active' && sub.status !== 'active') return false;
    if (filter === 'expired' && sub.status === 'active') return false;

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        sub.user_email?.toLowerCase().includes(search) ||
        sub.user_name?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  // Quick upgrade to Pro
  const upgradeUser = async (userId, userName) => {
    if (!window.confirm(`Upgrade ${userName} to PRO?`)) return;

    try {
      await axios.put(`/subscriptions/admin/${userId}`, { planType: 'pro' });
      alert('User upgraded to PRO successfully!');
      loadData();
      
      // Dispatch event to notify other components to refresh subscription data
      window.dispatchEvent(new Event('subscription-updated'));
    } catch (error) {
      console.error('Failed to upgrade:', error);
      alert('Failed to upgrade: ' + error.message);
    }
  };

  // Downgrade to Free
  const downgradeUser = async (userId, userName) => {
    if (!window.confirm(`Downgrade ${userName} to FREE? They will lose Pro features.`)) return;

    try {
      await axios.put(`/subscriptions/admin/${userId}`, { planType: 'free' });
      alert('User downgraded to FREE successfully!');
      loadData();
      
      // Dispatch event to notify other components to refresh subscription data
      window.dispatchEvent(new Event('subscription-updated'));
    } catch (error) {
      console.error('Failed to downgrade:', error);
      alert('Failed to downgrade: ' + error.message);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      paddingTop: '5rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '0.5rem'
        }}>
          💳 Subscription Management
        </h1>
        <p style={{ color: '#9ca3af', fontSize: '1.1rem' }}>
          Monitor subscription metrics and manage user plans
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'rgba(194, 85, 92, 0.1)',
            border: '1px solid rgba(194, 85, 92, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c2555c' }}>
              {stats.total}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Total Subscriptions</div>
          </div>

          <div style={{
            background: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#eab308' }}>
              {stats.pro}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Pro Users</div>
          </div>

          <div style={{
            background: 'rgba(156, 163, 175, 0.1)',
            border: '1px solid rgba(156, 163, 175, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9ca3af' }}>
              {stats.free}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Free Users</div>
          </div>

          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#22c55e' }}>
              {stats.active}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Active</div>
          </div>

          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {stats.recent}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>New (30d)</div>
          </div>

          <div style={{
            background: 'rgba(168, 85, 247, 0.1)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#a855f7' }}>
              {stats.conversionRate}%
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Conversion Rate</div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {['all', 'pro', 'free', 'active', 'expired'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '0.75rem 1.5rem',
                background: filter === f
                  ? 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: filter === f ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: filter === f ? '#141517' : 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Search by user name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '1rem',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '1rem'
          }}
        />
      </div>

      {/* Subscriptions Table */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
            Loading subscriptions...
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
            No subscriptions found
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(234, 179, 8, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#eab308', fontWeight: 'bold' }}>User</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#eab308', fontWeight: 'bold' }}>Plan</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#eab308', fontWeight: 'bold' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#eab308', fontWeight: 'bold' }}>Started</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#eab308', fontWeight: 'bold' }}>Expires</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#eab308', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub, idx) => (
                <tr key={sub.id} style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  background: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.2)' : 'transparent'
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ color: 'white', fontWeight: '500' }}>
                        {sub.user_name || 'Unknown User'}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        {sub.user_email}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      background: sub.plan_type === 'pro'
                        ? 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)'
                        : 'rgba(156, 163, 175, 0.2)',
                      color: sub.plan_type === 'pro' ? '#141517' : '#9ca3af'
                    }}>
                      {sub.plan_type?.toUpperCase() || 'FREE'}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      background: sub.status === 'active'
                        ? 'rgba(34, 197, 94, 0.2)'
                        : 'rgba(194, 85, 92, 0.2)',
                      color: sub.status === 'active' ? '#22c55e' : '#c2555c',
                      border: `1px solid ${sub.status === 'active' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(194, 85, 92, 0.3)'}`
                    }}>
                      {sub.status?.toUpperCase() || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                    {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                    {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      {sub.plan_type === 'free' ? (
                        <button
                          onClick={() => upgradeUser(sub.user_id, sub.user_name)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#141517',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          ⬆️ Upgrade
                        </button>
                      ) : (
                        <button
                          onClick={() => downgradeUser(sub.user_id, sub.user_name)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'rgba(156, 163, 175, 0.2)',
                            border: '1px solid rgba(156, 163, 175, 0.3)',
                            borderRadius: '6px',
                            color: '#9ca3af',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          ⬇️ Downgrade
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary Info */}
      <div style={{
        marginTop: '2rem',
        padding: '1.5rem',
        background: 'rgba(234, 179, 8, 0.1)',
        border: '1px solid rgba(234, 179, 8, 0.3)',
        borderRadius: '12px'
      }}>
        <h3 style={{ color: '#eab308', marginBottom: '1rem', fontSize: '1.2rem' }}>
          📊 Quick Insights
        </h3>
        <div style={{ color: '#9ca3af', lineHeight: '1.8' }}>
          <div>• Showing <strong style={{ color: 'white' }}>{filteredSubscriptions.length}</strong> of <strong style={{ color: 'white' }}>{subscriptions.length}</strong> subscriptions</div>
          <div>• Pro users make up <strong style={{ color: '#eab308' }}>{stats?.conversionRate}%</strong> of all subscriptions</div>
          <div>• <strong style={{ color: '#22c55e' }}>{stats?.active}</strong> subscriptions are currently active</div>
          <div>• <strong style={{ color: '#3b82f6' }}>{stats?.recent}</strong> new subscriptions in the last 30 days</div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionsPage;
